import { shouldSwapDimensions } from '@v2/components/imageHandling/helpers';
import {
  Annotation,
  AnnotationPoint,
  AnnotationType,
  ArrowAnnotation,
  CircleAnnotation,
  HighlighterAnnotation,
  PenAnnotation,
  RebaseCoordinatesParams,
  RectangleAnnotation,
  TextAnnotation,
} from '../types';

/**
 * Maps an image-space (un-rotated-original) point to a screen-space (working-viewport) point,
 * mirroring ProgressiveImage's CSS layout:
 *   1. The image is letterboxed (resizeMode='contain') inside a pre-rotation container whose
 *      dimensions are the viewport's dimensions *swapped* for 90°/270°.
 *   2. That container is centered in the viewport.
 *   3. The container is rotated around the viewport center.
 */
const imageToScreen = (
  p: AnnotationPoint,
  image: { widthOriginal: number; heightOriginal: number },
  viewport: { viewportWidth: number; viewportHeight: number },
  rotation: number
): AnnotationPoint => {
  const { widthOriginal, heightOriginal } = image;
  const { viewportWidth, viewportHeight } = viewport;
  const swap = shouldSwapDimensions(rotation);
  const containerW = swap ? viewportHeight : viewportWidth;
  const containerH = swap ? viewportWidth : viewportHeight;

  const scale = Math.min(containerW / widthOriginal, containerH / heightOriginal) || 1;
  const letterOffX = (containerW - widthOriginal * scale) / 2;
  const letterOffY = (containerH - heightOriginal * scale) / 2;
  // Letterboxed point in pre-rotation container coords:
  const lbX = p.x * scale + letterOffX;
  const lbY = p.y * scale + letterOffY;
  // Container is centered in the viewport.
  const contOffX = (viewportWidth - containerW) / 2;
  const contOffY = (viewportHeight - containerH) / 2;
  const preRotX = contOffX + lbX;
  const preRotY = contOffY + lbY;

  if (!rotation) return { x: preRotX, y: preRotY };

  const cx = viewportWidth / 2;
  const cy = viewportHeight / 2;
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = preRotX - cx;
  const dy = preRotY - cy;
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
};

/** Uniform size-rebase factor (matches the image's uniform resizeMode='contain' scale). */
const getDisplayScale = (
  image: { widthOriginal: number; heightOriginal: number },
  viewport: { viewportWidth: number; viewportHeight: number },
  rotation: number
) => {
  const swap = shouldSwapDimensions(rotation);
  const containerW = swap ? viewport.viewportHeight : viewport.viewportWidth;
  const containerH = swap ? viewport.viewportWidth : viewport.viewportHeight;
  return Math.min(containerW / image.widthOriginal, containerH / image.heightOriginal) || 1;
};

/**
 * Converts an annotation stored in un-rotated original image pixel space back into
 * the current working viewport space for display / editing.
 */
export const fromOriginalCoordinates = ({
  annotation,
  image,
  viewport,
  documentRotation = 0,
}: RebaseCoordinatesParams): Annotation => {
  const scale = getDisplayScale(image, viewport, documentRotation);
  const toSize = (v: number) => v * scale;
  const toPoint = (p: AnnotationPoint) => imageToScreen(p, image, viewport, documentRotation);

  const rebasedStyle = {
    ...annotation.style,
    strokeWidth: toSize(annotation.style.strokeWidth),
  };

  switch (annotation.type) {
    case AnnotationType.Pen:
    case AnnotationType.Highlighter: {
      const a = annotation as PenAnnotation | HighlighterAnnotation;
      return {
        ...a,
        style: rebasedStyle,
        points: a.points.map(toPoint),
      };
    }
    case AnnotationType.Arrow: {
      const a = annotation as ArrowAnnotation;
      return {
        ...a,
        style: rebasedStyle,
        start: toPoint(a.start),
        end: toPoint(a.end),
      };
    }
    case AnnotationType.Rectangle: {
      const a = annotation as RectangleAnnotation;
      const origin = toPoint(a.origin);
      const end = toPoint({ x: a.origin.x + a.width, y: a.origin.y + a.height });
      return {
        ...a,
        style: rebasedStyle,
        origin: { x: Math.min(origin.x, end.x), y: Math.min(origin.y, end.y) },
        width: Math.abs(end.x - origin.x),
        height: Math.abs(end.y - origin.y),
      };
    }
    case AnnotationType.Circle: {
      const a = annotation as CircleAnnotation;
      return {
        ...a,
        style: rebasedStyle,
        center: toPoint(a.center),
        radiusX: toSize(a.radiusX),
        radiusY: toSize(a.radiusY),
      };
    }
    case AnnotationType.Text: {
      const a = annotation as TextAnnotation;
      return {
        ...a,
        style: rebasedStyle,
        position: toPoint(a.position),
        fontSize: toSize(a.fontSize),
      };
    }
    default:
      return annotation;
  }
};
